<template>
  <div v-if="showActivity" class="fixed bottom-4 left-4 z-40">
    <div :class="`border rounded-lg shadow-lg p-4 ${expandedView ? 'max-w-lg w-96' : 'max-w-sm'} transition-all duration-300 ${applyThemeClasses('layout.card')}`">
      <div class="flex items-center justify-between mb-2">
        <h4 :class="`font-semibold text-sm ${applyThemeClasses('text.primary')}`">💰 Active Bids</h4>
        <button 
          @click="showActivity = false"
          :class="`hover:opacity-70 text-xs ${applyThemeClasses('text.secondary')}`"
        >
          ×
        </button>
      </div>

      <!-- Bid Summary -->
      <div v-if="bidSummary.totalActiveBids > 0" class="space-y-3">
        <div :class="`text-sm ${applyThemeClasses('text.secondary')}`">
          <div class="flex justify-between items-center mb-2">
            <span>Total Active Bids:</span>
            <span class="font-semibold">{{ bidSummary.totalActiveBids }}</span>
          </div>
          <div v-if="bidSummary.expiringToday > 0" class="text-red-600 font-semibold">
            ⚠️ {{ bidSummary.expiringToday }} bid{{ bidSummary.expiringToday > 1 ? 's' : '' }} expiring today!
          </div>
        </div>

        <!-- Toggle Expanded View -->
        <div class="flex justify-between items-center mb-2">
          <button
            @click="expandedView = !expandedView"
            :class="`text-xs px-2 py-1 rounded ${applyThemeClasses('button.secondary')}`"
          >
            {{ expandedView ? 'Compact View' : 'Detailed View' }}
          </button>
        </div>

        <!-- Recent High Bids -->
        <div class="border-t pt-2">
          <h5 :class="`text-xs font-semibold mb-1 ${applyThemeClasses('text.primary')}`">
            {{ expandedView ? 'All Active Bids:' : 'Highest Offers:' }}
          </h5>
          <div :class="`space-y-1 overflow-y-auto ${expandedView ? 'max-h-64' : 'max-h-32'}`">
            <div 
              v-for="bid in expandedView ? allActiveBids : bidSummary.highestBids.slice(0, 3)" 
              :key="bid.id"
              :class="`text-xs p-2 rounded border cursor-pointer hover:opacity-80 ${
                bid.expiresDay - gameStore.currentDay <= 1 
                  ? 'border-red-300 bg-red-50' 
                  : applyThemeClasses('layout.divider')
              }`"
              @click="viewBid(bid)"
            >
              <div class="font-semibold">{{ bid.chocoboName }}</div>
              <div class="flex justify-between items-center">
                <span>{{ bid.amount.toLocaleString() }}G</span>
                <span :class="applyThemeClasses('text.secondary')">
                  {{ getDaysRemaining(bid) }} days left
                </span>
              </div>
              <div class="text-xs italic">from {{ bid.bidderRanchName }}</div>
              
              <!-- Quick Actions in Expanded View -->
              <div v-if="expandedView" class="flex gap-2 mt-2">
                <button
                  @click.stop="acceptBid(bid)"
                  :class="`text-xs px-2 py-1 rounded ${applyThemeClasses('button.success')}`"
                >
                  Accept
                </button>
                <button
                  @click.stop="rejectBid(bid)"
                  :class="`text-xs px-2 py-1 rounded ${applyThemeClasses('button.danger')}`"
                >
                  Reject
                </button>
                <button
                  @click.stop="viewChocobo(bid.chocoboId)"
                  :class="`text-xs px-2 py-1 rounded ${applyThemeClasses('button.secondary')}`"
                >
                  View Chocobo
                </button>
              </div>
            </div>
          </div>
          
          <button 
            v-if="!expandedView && bidSummary.totalActiveBids > 3"
            @click="$emit('view-all-bids')"
            :class="`mt-2 w-full text-xs py-1 rounded ${applyThemeClasses('button.primary')}`"
          >
            View All Bids ({{ bidSummary.totalActiveBids }})
          </button>
        </div>
      </div>

      <!-- No Bids Message -->
      <div v-else :class="`text-sm text-center py-4 ${applyThemeClasses('text.secondary')}`">
        No active bids on your chocobos
      </div>
    </div>
  </div>

  <!-- Toggle Button -->
  <button 
    v-if="!showActivity"
    @click="showActivity = true"
    :class="`fixed bottom-4 left-4 z-40 ${applyThemeClasses('button.primary')} rounded-full p-2 shadow-lg transition-colors`"
    title="Show Active Bids"
  >
    <span class="text-sm">💵</span>
    <span 
      v-if="bidSummary.totalActiveBids > 0" 
      :class="`absolute -top-1 -right-1 ${
        bidSummary.expiringToday > 0 ? 'bg-red-500' : 'bg-blue-500'
      } text-white text-xs rounded-full w-5 h-5 flex items-center justify-center`"
    >
      {{ bidSummary.totalActiveBids }}
    </span>
  </button>
</template>

<script setup>
import { ref, computed, defineEmits } from 'vue'
import { useGameStore } from '@/store/game'
import { BidManager } from '@/services/bidManager'
import { applyThemeClasses } from '@/utils/themeColors'

const emit = defineEmits(['view-bid', 'view-all-bids', 'view-chocobo'])
const gameStore = useGameStore()
const showActivity = ref(false)
const expandedView = ref(false)

const bidSummary = computed(() => {
  return BidManager.getBidSummary(gameStore)
})

const allActiveBids = computed(() => {
  return BidManager.getActiveBids(gameStore).sort((a, b) => b.amount - a.amount)
})

const getDaysRemaining = (bid) => {
  return Math.max(0, bid.expiresDay - gameStore.currentDay)
}

const viewBid = (bid) => {
  emit('view-bid', bid)
  showActivity.value = false
}

const acceptBid = (bid) => {
  if (confirm(`Accept bid of ${bid.amount.toLocaleString()}G for ${bid.chocoboName}?`)) {
    const success = BidManager.acceptBid(bid.id, gameStore)
    if (success) {
      gameStore.saveGame()
      // Refresh the bids
      showActivity.value = false
      setTimeout(() => showActivity.value = true, 100)
    }
  }
}

const rejectBid = (bid) => {
  if (confirm(`Reject bid from ${bid.bidderName} for ${bid.chocoboName}?`)) {
    const success = BidManager.rejectBid(bid.id, gameStore)
    if (success) {
      gameStore.saveGame()
      // Refresh the bids
      showActivity.value = false
      setTimeout(() => showActivity.value = true, 100)
    }
  }
}

const viewChocobo = (chocoboId) => {
  emit('view-chocobo', chocoboId)
  showActivity.value = false
}
</script>